import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { BehaviorSubject, Observable, catchError, concatMap, filter, map, take, throwError } from 'rxjs';
import { ConfigurationService } from '../utils/configuration.service';

@Injectable({
  providedIn: 'root',
})
export class ImageService {
  private _imageTokenSubject$ = new BehaviorSubject<IImageToken | null>(null);
  private _tokenFetchInProgress = false;
  private _tokenUrl: string;
  private _apiUrl: string;

  constructor(
    private _http: HttpClient,
    private _configurationService: ConfigurationService
  ) {
    this._tokenUrl = environment.imageTokenEndpoint;
    this._apiUrl = `${this._configurationService.getApiUrl()}/api/internal/`;
  }

  public getAuthenticatedUrl(imageUrl: string): Observable<IAuthenticatedImageUrl> {
    return this._getImageToken().pipe(
      take(1),
      map((tokenData) => {
        const resizedImagesToken = tokenData.resizedimages.sasToken;
        const rawImagesToken = tokenData.rawimages.sasToken;

        if (!imageUrl) {
          return { authUrl: null, fallbackUrl: null };
        }

        let authUrl: string, fallbackUrl: string;

        if (imageUrl.includes('resizedimages')) {
          (authUrl = imageUrl + resizedImagesToken),
            (fallbackUrl = imageUrl.replace('resizedimages', 'rawimages') + rawImagesToken);
        } else if (imageUrl.includes('rawimages')) {
          (authUrl = imageUrl + rawImagesToken), (fallbackUrl = null);
        } else {
          (authUrl = null), (fallbackUrl = null);
        }

        return {
          authUrl,
          fallbackUrl,
        };
      })
    );
  }

  private _getImageToken(): Observable<IImageToken> {
    // this cache is implemented to avoid multiple token requests
    // when loading many images simultaneously, like the items catalog.

    // if we have a token stored and it hasn't expired yet, we return it
    if (this._imageTokenSubject$.value && this._isTokenValid()) {
      return this._imageTokenSubject$.asObservable().pipe(take(1));
    }

    // if there's no active request, we request the token and emit the subject
    if (!this._tokenFetchInProgress) {
      this._tokenFetchInProgress = true;
      return this._acquireToken();
    }

    // if a request is in progress we return the observable that will emit eventually
    // when value becomes different than null
    return this._imageTokenSubject$.asObservable().pipe(
      filter((token) => token !== null),
      take(1)
    );
  }

  private _acquireToken(): Observable<IImageToken> {
    return this._http.post<IImageToken>(`${this._apiUrl}${this._tokenUrl}`, {}).pipe(
      catchError((error) => {
        this._tokenFetchInProgress = false;
        return throwError(() => error);
      }),
      concatMap((token) => {
        this._imageTokenSubject$.next(token);
        this._tokenFetchInProgress = false;
        return this._imageTokenSubject$.asObservable().pipe(take(1));
      })
    );
  }

  private _isTokenValid(): boolean {
    const rawImagesExpiration = new Date(this._imageTokenSubject$.value?.rawimages.expiryTime);
    const resizedImagesExpiration = new Date(this._imageTokenSubject$.value?.resizedimages.expiryTime);
    const currentTime = new Date();
    return rawImagesExpiration > currentTime && resizedImagesExpiration > currentTime;
  }
}

export interface IImageToken {
  rawimages: IImageTokenData;
  resizedimages: IImageTokenData;
}

export interface IImageTokenData {
  sasToken: string;
  startTime: string;
  expiryTime: string;
}

export interface IAuthenticatedImageUrl {
  authUrl: string;
  fallbackUrl: string;
}
